import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Checkbox,
  Chip,
  EmptyState,
  ErrorState,
  FormActions,
  FormSection,
  PageHeader,
  Skeleton,
} from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';

import { RootState, store } from '../../../store/store';
import { setTermoInfo } from '../../../store/respostasDoSgaSlice';
import { salvarEtapaRascunho } from '../../../utils/adesaoFluxo';

import { gerarTermoPorCodigo } from '../../../api/termo-de-adesao/termoDeAdesaoApi';
import { buscarProdutosVinculados } from '../../../api/sga-produto/sga-produtoApi';
import { buscarUsuarioPorId } from '../../../api/user/userApi';
import { calcularRateio } from '../../../api/rateio/rateioApi';
import { listarTiposDeTermo, TipoDeTermo } from '../../../api/termo-template/termoTemplateApi';
import { parseMoeda } from '../../../utils/moeda';
import { ContratoLista, abrirPdfContrato } from '../../../api/contrato/contratoApi';
import { enviarContratoPorWhatsApp } from '../../../api/contrato/entregaContratoApi';
import { rotuloStatusContrato } from '../../../utils/contratoStatus';
import {
  listarContratosDaAdesao,
  nomeDoContrato,
  resolverVeiculoDoFluxo,
} from '../../../utils/termosDaAdesao';

import { ASSINATURA } from '../../assinatura/routes';

/**
 * `/termo` — **etapa 5 dos TRÊS fluxos** (reescrita na at-60; múltipla na at-81).
 *
 * ⚠️ Sem sobretítulo, pela mesma razão de `/produto`: adesão e as duas
 * substituições chegam aqui (`plan-c` §1.4).
 *
 * ## O que mudou na at-81 (feedback de campo, 03/11/2026)
 *
 * > *"Eu gerei o termo de adesão, e depois o outro eu não consegui gerar… não vi
 * > se tava sinalizando erro."* — e — *"gera o termo e não aparece o termo."*
 *
 *   1. 🔴 **Vários termos, de uma vez** (decisão de Victor). A escolha voltou a ser
 *      MÚLTIPLA — agora de verdade, com o `Checkbox` do kit. A at-60 trocou o
 *      checkbox por radio porque o grupo antigo *agia* como radio; o pedido do
 *      campo é o contrário: uma adesão precisa de proteção completa **e**
 *      comodato do rastreador.
 *   2. 🔴 **A lista de termos gerados vem do BANCO** (`utils/termosDaAdesao`). A
 *      tela guardava UM `contratoId` em estado local: gerar o segundo descartava o
 *      primeiro, e a assinatura assinava só o último — o outro ficava
 *      "Aguardando assinatura" para sempre.
 *   3. **Falha é dita POR TERMO**, junto do tipo que falhou, e não impede os
 *      outros. Antes era um toast único que passava.
 *   4. 🔴 **O PDF não é mais aberto sozinho depois de gerar.** A abertura
 *      automática vinha depois do `await` da geração, e celular bloqueia
 *      `window.open` fora do gesto — o PDF simplesmente não aparecia. Agora cada
 *      termo gerado tem "Ler", que abre a aba no próprio clique.
 *
 * ⚠️ **Tipo já gerado nesta adesão fica marcado e travado.** Gerá-lo de novo
 * criaria um segundo contrato pendente do mesmo tipo, e a assinatura em lote
 * assinaria os dois. Refazer um termo é distrato, que é do TI.
 *
 * ⚠️ **O que NÃO mudou:** a montagem do payload de `POST /termo/gerar/:codigo`
 * (~35 campos lidos dos slices por nome), a resolução do veículo pela placa, a
 * base de faixa do rateio pelo valor protegido (at-30) e o `codigoTermo` no
 * slice — é ele que a régua do fluxo lê para a etapa 5.
 */

type ResultadoPorTipo = Record<string, { erro: string }>;

const TermoPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const [veiculoId, setVeiculoId] = useState<string | null>(null);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [falhas, setFalhas] = useState<ResultadoPorTipo>({});

  const [termosCatalogo, setTermosCatalogo] = useState<TipoDeTermo[]>([]);
  const [carregandoTermos, setCarregandoTermos] = useState(true);
  const [erroTermos, setErroTermos] = useState<string | null>(null);

  const [contratos, setContratos] = useState<ContratoLista[]>([]);
  const [erroContratos, setErroContratos] = useState<string | null>(null);

  const [gerando, setGerando] = useState(false);
  const [enviandoWhatsAppDe, setEnviandoWhatsAppDe] = useState<string | null>(null);
  const [abrindoPdfDe, setAbrindoPdfDe] = useState<string | null>(null);

  const token = useSelector((state: RootState) => state.auth.accessToken);
  const [cpfCnpjVoluntario, setCpfCnpjVoluntario] = useState<string | null>(null);
  const [nomeCompletoVoluntario, setNomeCompletoVoluntario] = useState<string | null>(null);
  const codigoTermoAtual = useSelector((state: RootState) => state.respostasDoSga.codigoTermo);
  const associadoId = useSelector((state: RootState) => state.associado.associadoId);
  const voluntarioId = useSelector((state: RootState) => state.auth.userId);
  const nome = useSelector((state: RootState) => state.associado.nomeCompleto);
  const dataNascimento = useSelector((state: RootState) => state.associado.dataNascimento);
  const cpfCnpj = useSelector((state: RootState) => state.associado.cpfCnpj);
  const rg = useSelector((state: RootState) => state.associado.rg);
  const dddCelular = useSelector((state: RootState) => state.associado.dddCelular);
  const celular = useSelector((state: RootState) => state.associado.celular);
  const email = useSelector((state: RootState) => state.associado.email);
  const endereco = useSelector((state: RootState) => state.associado.carro?.endereco.rua);
  const numero = useSelector((state: RootState) => state.associado.carro?.endereco.numero);
  const complemento = useSelector(
    (state: RootState) => state.associado.carro?.endereco.complemento,
  );
  const bairro = useSelector((state: RootState) => state.associado.carro?.endereco.bairro);
  const cidade = useSelector((state: RootState) => state.associado.carro?.endereco.cidade);
  const estado = useSelector((state: RootState) => state.associado.carro?.endereco.estado);
  const cep = useSelector((state: RootState) => state.associado.carro?.endereco.cep);
  const placa = useSelector((state: RootState) => state.associado.carro?.placa);
  const chassi = useSelector((state: RootState) => state.associado.carro?.chassi);
  const renavam = useSelector((state: RootState) => state.associado.carro?.renavam);
  const codigoFipe = useSelector((state: RootState) => state.associado.carro?.codigoFipe);
  const valorProtegido = useSelector((state: RootState) => state.associado.carro?.valorProtegido);
  const anoFabricacao = useSelector((state: RootState) => state.associado.carro?.anoFabricacao);
  const anoModelo = useSelector((state: RootState) => state.associado.carro?.anoModelo);
  const valorFipe = useSelector((state: RootState) => state.associado.carro?.valorFipe);
  const marca = useSelector((state: RootState) => state.associado.carro?.marcaNome);
  const modelo = useSelector((state: RootState) => state.associado.carro?.modelo);
  const categoria = useSelector((state: RootState) => state.associado.carro?.categoriaNome);
  const tipo = useSelector((state: RootState) => state.associado.carro?.tipoVeiculoNome);
  const cor = useSelector((state: RootState) => state.associado.carro?.corNome);
  const vencimento = useSelector((state: RootState) => state.associado.carro?.vencimentoDesejado);
  const codigoRegional = useSelector((state: RootState) => state.associado.regionalId);
  const codigoTipoVeiculo = useSelector(
    (state: RootState) => state.associado.carro?.tipoVeiculoId,
  );
  const valorAdesao = useSelector((state: RootState) => state.associado.carro?.valorAdesao);
  const valorCota = useSelector((state: RootState) => state.associado.carro?.valorCota);

  const carregarTermos = useCallback(async () => {
    if (!token) return;
    setCarregandoTermos(true);
    setErroTermos(null);
    try {
      const catalogo = await listarTiposDeTermo();
      setTermosCatalogo(catalogo);
    } catch (error: any) {
      setErroTermos(error?.message || 'Erro ao carregar os tipos de termo.');
    } finally {
      setCarregandoTermos(false);
    }
  }, [token]);

  useEffect(() => {
    void carregarTermos();
  }, [carregarTermos]);

  useEffect(() => {
    const buscarVeiculoDoAssociado = async () => {
      if (!associadoId) return;
      try {
        const id = await resolverVeiculoDoFluxo(associadoId, placa);
        if (id) {
          setVeiculoId(id);
        } else {
          toast.warn('Nenhum veículo encontrado para este associado.');
        }
      } catch (error) {
        toast.error('Erro ao buscar o veículo do associado.');
      }
    };
    void buscarVeiculoDoAssociado();
  }, [associadoId, placa]);

  const carregarContratos = useCallback(async () => {
    if (!associadoId || !veiculoId) return;
    setErroContratos(null);
    try {
      setContratos(await listarContratosDaAdesao(veiculoId, associadoId));
    } catch (erro: any) {
      setErroContratos(erro?.message || 'Não foi possível carregar os termos já gerados.');
    }
  }, [associadoId, veiculoId]);

  useEffect(() => {
    void carregarContratos();
  }, [carregarContratos]);

  useEffect(() => {
    const buscarDadosVoluntario = async () => {
      if (!voluntarioId || !token) return;
      try {
        const voluntario = await buscarUsuarioPorId(voluntarioId);
        setCpfCnpjVoluntario(voluntario.cpfCnpj);
        setNomeCompletoVoluntario(voluntario.nomeCompleto);
      } catch (error) {
        toast.error('Erro ao buscar dados do voluntário.');
      }
    };
    void buscarDadosVoluntario();
  }, [voluntarioId, token]);

  /** Tipos que já têm contrato vigente nesta adesão — marcados e travados. */
  const tiposGerados = useMemo(
    () => new Set(contratos.map((c) => String(c.codigoTipo ?? ''))),
    [contratos],
  );

  const nomeDoTipo = useCallback(
    (codigo: string | null) =>
      termosCatalogo.find((t) => t.codigo === codigo)?.nome ?? null,
    [termosCatalogo],
  );

  const alternarSelecao = (codigo: string, marcado: boolean) => {
    setSelecionados((atuais) =>
      marcado ? [...atuais, codigo] : atuais.filter((c) => c !== codigo),
    );
  };

  /** O payload é o mesmo para todos os tipos — montado UMA vez por clique. */
  const montarPayload = async (idVeiculo: string) => {
    const produtosVinculados = await buscarProdutosVinculados(idVeiculo);
    const produtosFormatados = produtosVinculados.map((produto) => ({
      descricao: produto.descricao_produto,
      observacao: '',
      valor: produto.valor_produto,
    }));

    // at-30 (item 18): o rateio por faixa usa o valor PROTEGIDO (fallback FIPE) —
    // mesma base da tela de produtos, para a mensalidade exibida lá bater com o termo
    const valorBaseFaixa = parseMoeda(valorProtegido || valorFipe || '');
    const rateioResponse = await calcularRateio(codigoRegional!, codigoTipoVeiculo!, valorBaseFaixa);

    const formatarData = (data: string) => {
      const [ano, mes, dia] = data.split('-');
      return `${dia}/${mes}/${ano}`;
    };

    return {
      idAssociado: associadoId,
      idVeiculo: idVeiculo,
      idVoluntario: voluntarioId,
      aceiteDigital: false,
      nome,
      dataNascimento: formatarData(dataNascimento),
      cpf: cpfCnpj,
      rg,
      telefone: `${dddCelular} ${celular}`,
      celular: `${dddCelular} ${celular}`,
      email,
      endereco,
      numero,
      complemento,
      bairro,
      cidadeEstado: `${cidade} / ${estado}`,
      cep,
      placa,
      chassi,
      renavam,
      codigoFipe,
      valorProtegido,
      valorFipe,
      anoFabricacao,
      anoModelo,
      marca,
      modelo,
      categoria,
      tipo,
      cor,
      valorAdesao,
      vencimento,
      observacao: '',
      valorTotal: '',
      consultor: nomeCompletoVoluntario,
      cpfConsultor: cpfCnpjVoluntario,
      rateio: rateioResponse.valorRateio,
      taxaAdministrativa: valorCota,
      produtos: produtosFormatados,
    };
  };

  const handleGerarTermos = async () => {
    if (selecionados.length === 0) {
      toast.warn('Marque ao menos um termo antes de gerar.');
      return;
    }
    if (!associadoId || !veiculoId) {
      toast.warn('Código do associado ou veículo não encontrado.');
      return;
    }

    setGerando(true);
    setFalhas({});

    let payload: Awaited<ReturnType<typeof montarPayload>>;
    try {
      payload = await montarPayload(veiculoId);
    } catch (erro: any) {
      // Sem produtos ou rateio não há termo nenhum — falha da geração inteira.
      toast.error(erro?.message || 'Não foi possível preparar os dados do termo.');
      setGerando(false);
      return;
    }

    const novasFalhas: ResultadoPorTipo = {};
    const gerados: string[] = [];

    // Em sequência, e não em paralelo: cada geração consulta compulsórios, monta o
    // PDF e sobe ao GCS — em rajada no 4G, uma derrubaria a outra por timeout.
    for (const codigo of selecionados) {
      try {
        const resposta = await gerarTermoPorCodigo(codigo, payload);
        if (!resposta?.contrato?.id) {
          // O PDF existiu, mas o contrato não foi registrado (o backend engole essa
          // falha para não perder o documento). Sem contrato não há o que assinar.
          throw new Error('O termo foi gerado, mas o contrato não foi registrado. Tente de novo.');
        }
        gerados.push(codigo);
      } catch (erro: any) {
        novasFalhas[codigo] = { erro: erro?.message || 'Erro ao gerar este termo.' };
      }
    }

    setFalhas(novasFalhas);
    setSelecionados(Object.keys(novasFalhas));

    if (gerados.length > 0) {
      // `codigoTermo` é o que a régua do fluxo lê para a etapa 5 — basta um.
      if (!codigoTermoAtual) {
        dispatch(setTermoInfo({ codigoEmissao: '', codigoTermo: gerados[0] }));
      }
      // Rascunho (at-38): termo gerado, falta a assinatura.
      void salvarEtapaRascunho(dispatch, () => store.getState(), 'ASSINATURA', {
        associadoId,
      });
    }

    await carregarContratos();
    setGerando(false);

    if (gerados.length > 0 && Object.keys(novasFalhas).length === 0) {
      toast.success(
        gerados.length === 1 ? 'Termo gerado com sucesso!' : `${gerados.length} termos gerados com sucesso!`,
      );
    } else if (gerados.length > 0) {
      toast.warn('Parte dos termos não foi gerada — veja o motivo ao lado de cada um.');
    } else {
      toast.error('Nenhum termo foi gerado — veja o motivo ao lado de cada um.');
    }
  };

  /** Abre o PDF. ⚠️ Chamado DIRETO no clique: a aba precisa nascer no gesto. */
  const lerContrato = async (contratoId: string) => {
    setAbrindoPdfDe(contratoId);
    try {
      const abriuEmAba = await abrirPdfContrato(contratoId);
      if (!abriuEmAba) toast.info('O navegador bloqueou a aba nova — o termo foi baixado.');
    } catch (erro: any) {
      toast.error(erro?.message ?? 'Não foi possível abrir o termo.');
    } finally {
      setAbrindoPdfDe(null);
    }
  };

  /**
   * Envia o contrato ao associado.
   *
   * O backend decide o canal: com o OctaDesk ligado, a mensagem sai automaticamente
   * pelo template aprovado; desligado, indisponível ou sem celular no cadastro, ele
   * devolve o link e caímos no `wa.me` manual — que é o comportamento de sempre.
   *
   * ⚠️ O link vem do backend com **token de entrega**: o PDF é privado desde a at-40,
   * e o associado não tem sessão para abrir o proxy autenticado.
   */
  const enviarMensagemWhatsApp = async (contratoId: string) => {
    setEnviandoWhatsAppDe(contratoId);
    try {
      const resultado = await enviarContratoPorWhatsApp(contratoId);

      if (resultado.enviado) {
        toast.success(resultado.mensagem);
        return;
      }

      // Sem link não há fallback possível — abrir o WhatsApp com "undefined" no texto
      // seria pior que não abrir. O backend já explica na mensagem o que falta.
      if (!resultado.link) {
        toast.warn(resultado.mensagem);
        return;
      }

      toast.info(resultado.mensagem);
      const mensagem =
        `Prezado(a) ${nome}, parabéns pela sua adesão! Estamos felizes em tê-lo conosco. ` +
        `Você pode acessar seu contrato através do seguinte link: ${resultado.link}`;
      window.open(
        `https://api.whatsapp.com/send?text=${encodeURIComponent(mensagem)}`,
        '_blank',
      );
    } catch (erro: any) {
      toast.error(erro?.message ?? 'Não foi possível preparar o envio do contrato.');
    } finally {
      setEnviandoWhatsAppDe(null);
    }
  };

  const irParaAssinatura = () => {
    if (!associadoId || !veiculoId) {
      toast.warn('Dados do termo ou veículo estão incompletos.');
      return;
    }
    // A assinatura descobre os pendentes pelo backend — o `state` só poupa a
    // resolução do veículo de novo.
    navigate(ASSINATURA(), {
      state: {
        codigoTermo: codigoTermoAtual ?? '',
        associadoId,
        carroId: veiculoId,
      },
    });
  };

  const temContratos = contratos.length > 0;
  const quantosSelecionados = selecionados.length;

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Termo" aoVoltar={voltar} />}>
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={5} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormSection titulo="Selecione os termos" icone="adesoes">
                {carregandoTermos ? (
                  <Skeleton linhas={3} altura="tw-h-12" />
                ) : erroTermos ? (
                  <ErrorState
                    mensagem="Não foi possível carregar os tipos de termo."
                    detalhe={erroTermos}
                    aoTentarNovamente={() => void carregarTermos()}
                  />
                ) : termosCatalogo.length === 0 ? (
                  <EmptyState
                    titulo="Nenhum tipo de termo disponível"
                    descricao="O TI precisa cadastrar o texto das cláusulas em Administração de Termos para que o termo possa ser gerado."
                    icone="sem-dados"
                  />
                ) : (
                  // at-40: a rota devolve APENAS os tipos geráveis (ativos e com texto).
                  // Quem decide o que é gerável continua sendo o backend.
                  <div role="group" aria-label="Tipos de termo" className="tw-flex tw-flex-col tw-gap-3">
                    <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
                      Marque todos os termos que esta adesão precisa — eles são gerados de uma vez
                      e assinados juntos na próxima etapa.
                    </p>
                    {termosCatalogo.map((termo) => {
                      const jaGerado = tiposGerados.has(termo.codigo);
                      const falha = falhas[termo.codigo];
                      return (
                        <Checkbox
                          key={termo.codigo}
                          rotulo={termo.nome}
                          destaque
                          checked={jaGerado || selecionados.includes(termo.codigo)}
                          disabled={jaGerado || gerando}
                          motivoDesabilitado={jaGerado ? 'Já gerado nesta adesão.' : undefined}
                          descricao={
                            falha ? (
                              <span role="alert" className="tw-text-danger">
                                Não foi gerado: {falha.erro}
                              </span>
                            ) : undefined
                          }
                          onChange={(marcado) => alternarSelecao(termo.codigo, marcado)}
                          className="tw-rounded-xl tw-border tw-border-superficie-borda tw-p-3"
                        />
                      );
                    })}
                  </div>
                )}
              </FormSection>

              {erroContratos && (
                <div className="tw-mt-4">
                  <ErrorState
                    mensagem="Não foi possível carregar os termos já gerados."
                    detalhe={erroContratos}
                    aoTentarNovamente={() => void carregarContratos()}
                  />
                </div>
              )}

              {temContratos && (
                <div className="tw-mt-6">
                  <FormSection titulo="Termos gerados" icone="documento">
                    <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                      {contratos.map((contrato) => {
                        const status = rotuloStatusContrato(contrato.status);
                        return (
                          <li
                            key={contrato.id}
                            className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-superficie-borda tw-p-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                          >
                            <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
                              <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">
                                {nomeDoTipo(contrato.codigoTipo) ?? nomeDoContrato(contrato)}
                              </span>
                              <span>
                                <Chip variante={status.variante}>{status.texto}</Chip>
                              </span>
                            </div>
                            <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
                              <Botao
                                variante="secundario"
                                tamanho="sm"
                                carregando={abrindoPdfDe === contrato.id}
                                onClick={() => void lerContrato(contrato.id)}
                              >
                                Ler
                              </Botao>
                              <Botao
                                variante="secundario"
                                tamanho="sm"
                                carregando={enviandoWhatsAppDe === contrato.id}
                                disabled={enviandoWhatsAppDe !== null}
                                onClick={() => void enviarMensagemWhatsApp(contrato.id)}
                              >
                                Enviar por WhatsApp
                              </Botao>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </FormSection>
                </div>
              )}

              <FormActions
                apoio={
                  !associadoId
                    ? 'Nenhum cadastro em andamento — esta etapa é aberta depois de cadastrar o associado e o veículo.'
                    : temContratos
                      ? 'Leia os termos gerados com o associado e siga para a assinatura.'
                      : 'Gere os termos para liberar a assinatura.'
                }
              >
                <Botao
                  variante={temContratos ? 'secundario' : 'primario'}
                  larguraTotal={!temContratos}
                  carregando={gerando}
                  disabled={gerando || !veiculoId || quantosSelecionados === 0}
                  onClick={() => void handleGerarTermos()}
                >
                  {gerando
                    ? 'Gerando...'
                    : quantosSelecionados > 1
                      ? `Gerar termos (${quantosSelecionados})`
                      : 'Gerar termo'}
                </Botao>

                {temContratos && (
                  <Botao variante="primario" disabled={gerando} onClick={irParaAssinatura}>
                    Continuar — Assinatura
                  </Botao>
                )}
              </FormActions>
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default TermoPage;
